import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, API } from "@/lib/api";
import { BrandMark } from "@/components/BrandMark";
import {
  ShieldCheck, AlertTriangle, Fingerprint, Lock, Clock, FileText, ArrowLeft,
  BadgeCheck, KeyRound, Hash, User,
} from "lucide-react";

const Field = ({ icon: Icon, label, value, mono, testid }) => (
  <div className="border-b cad-hairline pb-2" data-testid={testid}>
    <div className="font-mono-cad text-[10px] uppercase tracking-[0.18em] text-[#7C93A5] flex items-center gap-1.5">
      {Icon && <Icon size={11} className="text-[#C99A2E]" />} {label}
    </div>
    <div className={`text-[#F6F8FA] mt-0.5 ${mono ? "font-mono-cad text-[12px] break-all" : "text-[13px]"}`}>{value ?? "—"}</div>
  </div>
);

export const VerifyEvidencePack = () => {
  const { packNumber } = useParams();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["verify-pack", packNumber],
    queryFn: async () => (await api.get(`/verify/pack/${packNumber}`)).data,
    retry: false,
  });

  const trustBadge = data?.certificate?.trust_badge === "green";
  const signed = data?.digital_signature?.status === "signed";

  return (
    <div className="min-h-screen p-6 lg:p-12" data-testid="page-verify-pack">
      <div className="flex items-center justify-between mb-8">
        <BrandMark size={140} />
        <Link to="/portal" className="cad-btn-outline px-3 py-1.5 rounded-sm text-[12px] inline-flex items-center gap-1.5">
          <ArrowLeft size={12} /> Public Portal
        </Link>
      </div>

      {isLoading && (
        <div className="max-w-3xl mx-auto cad-panel p-8 text-[#B3C2CC] text-[13px]" data-testid="verify-pack-loading">
          Verifying evidence pack…
        </div>
      )}

      {isError && (
        <div className="cad-panel p-8 max-w-xl mx-auto text-center" data-testid="verify-pack-not-found">
          <AlertTriangle className="mx-auto text-[#E67E7E]" size={32} />
          <div className="font-display text-xl font-bold text-[#F6F8FA] mt-3">Evidence pack not found</div>
          <div className="text-[13px] text-[#B3C2CC] mt-2">
            No public evidence pack was located for reference{" "}
            <span className="font-mono-cad text-[#F6F8FA]">{packNumber}</span>.
            {error?.response?.status === 503 && (
              <div className="mt-3 text-[11px] font-mono-cad uppercase tracking-widest text-[#C99A2E]">
                The public verification portal is currently offline.
              </div>
            )}
          </div>
        </div>
      )}

      {data && (
        <div className="max-w-4xl mx-auto cad-panel p-8 relative" data-testid="verify-pack-detail">
          <div className="absolute inset-0 cad-topo opacity-30 pointer-events-none"></div>
          <div className="relative flex items-start justify-between gap-4">
            <div>
              <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#C99A2E]">
                Cadastre360 · Compliance Evidence Pack
              </div>
              <div className="font-display text-3xl font-bold text-[#F6F8FA] mt-1" data-testid="verify-pack-number">
                {data.pack_number}
              </div>
              <div className="text-[13px] text-[#B3C2CC] mt-1">
                Issued by the Ministry of Mines and Mining Development, Republic of Zimbabwe
              </div>
            </div>
            <div className="text-right">
              <div className={`cad-chip ${trustBadge ? "cad-chip-green" : "cad-chip-gold"} inline-flex items-center gap-1 text-[10px] uppercase`}
                   data-testid="verify-pack-trust-badge">
                <BadgeCheck size={11} /> {trustBadge ? "Trusted Certificate" : "Amber · Self-Signed Ministry"}
              </div>
              <div className="mt-2 text-[11px] font-mono-cad uppercase tracking-widest text-[#7C93A5]">
                {data.verification_status}
              </div>
            </div>
          </div>

          <div className="relative mt-6 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
            <Field icon={Hash} label="Evidence number" value={data.pack_number} mono testid="verify-field-number" />
            <Field icon={ShieldCheck} label="Verification status" value={data.verification_status} mono testid="verify-field-status" />
            <Field icon={Clock} label="Generated at" value={data.timestamp} mono testid="verify-field-timestamp" />
            <Field icon={User} label="Officer" value={data.officer} mono testid="verify-field-officer" />
            <Field icon={Fingerprint} label="Digital fingerprint" value={data.digital_fingerprint} mono testid="verify-field-fingerprint" />
            <Field icon={Hash} label="SHA-256 checksum" value={data.sha256_checksum} mono testid="verify-field-checksum" />
            <Field icon={FileText} label="Audit reference" value={data.audit_number} mono testid="verify-field-audit" />
            <Field icon={FileText} label="Event count" value={data.event_count?.toLocaleString?.() ?? data.event_count} testid="verify-field-events" />
          </div>

          <div className="relative mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="cad-panel p-4" data-testid="verify-cert">
              <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E] flex items-center gap-1.5">
                <KeyRound size={11} /> Certificate validity
              </div>
              <div className="text-[13px] text-[#F6F8FA] mt-2 font-mono-cad break-all">
                {data.certificate?.common_name || "—"}
              </div>
              <div className="text-[11px] text-[#B3C2CC] mt-1 font-mono-cad">
                Issuer: {data.certificate?.issuer || "—"}
              </div>
              <div className="text-[11px] text-[#B3C2CC] mt-1 font-mono-cad">
                Not after: {data.certificate?.not_after || "—"}
              </div>
              <div className="text-[11px] text-[#B3C2CC] mt-1 font-mono-cad">
                Days remaining: {data.certificate?.days_remaining ?? "—"}
              </div>
              <div className="text-[11px] text-[#B3C2CC] mt-1 font-mono-cad">
                Algorithm: {data.certificate?.signature_algorithm || "—"}
              </div>
              <div className="text-[10px] text-[#7C93A5] mt-1 font-mono-cad">
                Fingerprint: {data.certificate?.sha256_fingerprint?.slice(0, 32)}…
              </div>
              <div className="text-[10px] text-[#7C93A5] mt-1 font-mono-cad uppercase tracking-widest">
                Revocation: {data.certificate?.revocation_status}
              </div>
            </div>

            <div className="cad-panel p-4" data-testid="verify-signature">
              <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E] flex items-center gap-1.5">
                <Lock size={11} /> Digital signature status
              </div>
              <div className={`mt-2 cad-chip ${signed ? "cad-chip-green" : "cad-chip-red"} inline-flex items-center gap-1 text-[10px] uppercase`}>
                {signed ? "Signed" : "Unsigned"}
              </div>
              <div className="text-[11px] text-[#B3C2CC] mt-2 font-mono-cad">
                Method: {data.digital_signature?.method || "not signed yet"}
              </div>
              <div className="text-[11px] text-[#B3C2CC] mt-1 font-mono-cad break-all">
                Digest: {data.digital_signature?.digest || "—"}
              </div>
              <div className="text-[11px] text-[#B3C2CC] mt-1 font-mono-cad">
                Signed at: {data.digital_signature?.signed_at || "—"}
              </div>
              {data.digital_signature?.rfc3161_timestamp && (
                <div className="text-[10px] text-[#7C93A5] mt-1 font-mono-cad">
                  RFC3161: {data.digital_signature.rfc3161_timestamp.timestamp}
                </div>
              )}
              <div className="text-[10px] text-[#7C93A5] mt-2 font-mono-cad uppercase tracking-widest">
                Tamper detection: {data.tamper_detection?.hash_intact ? "hash intact" : "tampered"}
              </div>
            </div>
          </div>

          <div className="relative mt-6 flex items-start justify-between gap-4">
            <div className="flex-1 text-[11px] text-[#7C93A5] font-mono-cad leading-relaxed">
              This is a public, read-only verification portal. No authentication is required. The document is
              considered authentic when the SHA-256 fingerprint, the digital signature status and the certificate
              validity window all resolve successfully. Any mismatch indicates tampering.
            </div>
            <img
              src={`${API}/verify/pack/${data.pack_number}/qr.png`}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              alt="Evidence pack QR"
              className="w-28 h-28 rounded-sm border cad-hairline bg-[#F6F8FA]"
              data-testid="verify-pack-qr"
            />
          </div>
        </div>
      )}
    </div>
  );
};
